<script setup lang="ts">
import { useTimeoutFn } from '@vueuse/core';
import { slugifyPath, type MessageData } from './parser';
import { CactusSportsbook } from '@/shared/SportsBook/CactusSportsbook';
import { copyToClipboard } from '@/utils/apostatudo/clipboard';

const { t } = useTexts();
const { isAuthenticated } = storeToRefs(useAuthStore());

const { setBscode } = useSportsBookStore();

const iframeElement = ref<HTMLIFrameElement | null>(null);
const iframeSrc = ref<string>('');
const cactusSportbooksInstance = CactusSportsbook.getInstance();
const isTimeoutEnabled = ref<boolean>(true);
const isIframeReady = ref<boolean>(false);
const pendingBscode = ref<string | null>(null);
const pendingSelectionId = ref<string | null>(null);

const layoutStore = useLayoutStore();
const { isLayoutMobileOrTablet } = useLayout();
const route = useRoute();
const { goToLocaleRoute } = useRoutes();
const { fullSportsPageUrl } = useSportsBook();
const { openLogin } = useAuth();
const { isStandAlone, isIosSafari } = useLayout();
const { updateWallet } = useWallet();
const { openDepositModal } = usePayments();

function goToHomePage(): void {
  cactusSportbooksInstance.goToPage({ eventlistTab: 'Early' });
}

async function selectCurrentPage() {
  try {
    if (!cactusSportbooksInstance.hasUrlSetup) {
      await fetchIframeUrl();
    }

    if (cactusSportbooksInstance.isAuthenticated !== isAuthenticated.value) {
      await fetchIframeUrl();
    }

    if (!iframeSrc.value && cactusSportbooksInstance.hasUrlSetup) {
      iframeSrc.value = cactusSportbooksInstance.baseUrl;
    }

    if (iframeElement.value) {
      cactusSportbooksInstance.updateConfig({
        iframeElement: iframeElement.value,
      });
    }

    const lastSegment = route.path.split('/').filter(Boolean).pop();
    const urlParams = lastSegment?.split('-') || [];
    const eventBscode = route.query.bscode;
    const eventlistTab = route.query.tab;
    const selectionIdQuery = route.query.selectionId;
    const eventTypeMap = {
      e: 'eventId',
      l: 'leagueId',
      s: 'sportId',
      ml: 'masterLeagueId',
      tab: 'eventlistTab',
      my: 'betting-history',
      open: 'open-bets',
    };

    let eventType = eventTypeMap[urlParams[0]] || urlParams[0];

    // Prioridade para query params
    if (eventBscode) {
      eventType = 'bscode';
    }

    if (eventlistTab) {
      eventType = 'eventlistTab';
    }

    // Quando vier ?selectionId=..., o Cactus resolve event+market sozinho
    // via getRedirectUrlInfo e adiciona ao betslip. Tem prioridade máxima
    // pra que o eventId acompanhante (do EventCard) não atrapalhe.
    if (selectionIdQuery) {
      eventType = 'selectionId';
    }

    const getFirst = (param) => (Array.isArray(param) ? param[0] : param);
    const bsCodeParam = getFirst(eventBscode);
    const eventlistTabParam = getFirst(eventlistTab);
    const selectionIdParam = getFirst(selectionIdQuery);
    const cactusPath = cactusSportbooksInstance.discoveryPathFromQueryParams({
      [eventType]:
        selectionIdParam || bsCodeParam || eventlistTabParam || urlParams[1],
    });

    if (eventBscode && !isIframeReady.value) {
      pendingBscode.value = bsCodeParam;

      return;
    }

    // selectionId precisa do iframe pronto pra que o handler interno
    // (getRedirectUrlInfo + addSelection) consiga resolver event/market.
    if (selectionIdQuery && !isIframeReady.value) {
      pendingSelectionId.value = selectionIdParam;

      return;
    }

    if (isIframeReady.value) {
      cactusSportbooksInstance.goToPage(cactusPath, () => {
        isTimeoutEnabled.value = false;
      });

      return;
    }

    useTimeoutFn(
      () => {
        cactusSportbooksInstance.goToPage(cactusPath, () => {
          isTimeoutEnabled.value = false;
        });
      },
      isTimeoutEnabled.value ? 3000 : 0,
    );
  } catch {
    goToHomePage();

    throw new Error('cactus sportsbook: impossible to select current page');
  }
}

function sendFooterToIframe() {
  debug('sendFooterToIframe', `https://${appUrl()}/_/footer`);

  cactusSportbooksInstance.sendPostMessage({
    eventType: 'footerDomain',
    eventData: { value: `https://${appUrl()}/_/footer` },
  });
}

function sendIsIosStandaloneDevice() {
  debug('sendIsIosStandaloneDevice', isStandAlone.value && isIosSafari.value);

  cactusSportbooksInstance.sendPostMessage({
    eventType: 'isIosStandaloneDevice',
    eventData: { value: isStandAlone.value && isIosSafari.value },
  });
}

async function fetchIframeUrl() {
  isIframeReady.value = false;
  pendingBscode.value = null;
  pendingSelectionId.value = null;

  const endpoint = isAuthenticated.value
    ? '/cactus-sportbook/launch'
    : '/cactus-sportbook/anonymous-launch';

  const { ApiService } = useApiService();

  const { data } = await ApiService.get(endpoint);

  iframeSrc.value = data.url;

  cactusSportbooksInstance.updateConfig({
    url: data.url,
    iframeElement: iframeElement.value,
  });

  isTimeoutEnabled.value = true;

  await selectCurrentPage();
}

watch(
  () => route.query,
  async () => {
    if (!Object.keys(route.query).length) {
      return;
    }

    await selectCurrentPage();
  },
);

watch(
  () => isAuthenticated.value,
  async () => {
    await fetchIframeUrl();
    await selectCurrentPage();
  },
);

let isProcessingLocationChange = false;

function listenPostMessage(message: MessageEvent) {
  if (message.source !== iframeElement.value?.contentWindow) {
    return;
  }

  let messageData = message.data as MessageData;

  if (typeof message.data === 'string') {
    try {
      messageData = JSON.parse(message.data);
    } catch {
      messageData = message.data;
    }
  }

  debug('listenPostMessage', messageData);

  if (messageData === 'update:balance') {
    updateWallet();
  }

  if (typeof messageData !== 'object') {
    return;
  }

  if (messageData.eventType === 'APP_READY') {
    isIframeReady.value = true;
    sendFooterToIframe();
    sendIsIosStandaloneDevice();

    if (pendingBscode.value) {
      const cactusPath = cactusSportbooksInstance.discoveryPathFromQueryParams({
        bscode: pendingBscode.value,
      });

      cactusSportbooksInstance.goToPage(cactusPath, () => {
        isTimeoutEnabled.value = false;
      });

      pendingBscode.value = null;
    }

    if (pendingSelectionId.value) {
      cactusSportbooksInstance.sendPostMessage({
        eventType: 'selectionId',
        eventData: { value: pendingSelectionId.value },
      });

      pendingSelectionId.value = null;
    }
  }

  if (messageData.eventType === 'BUTTON_REDIRECT') {
    goToLocaleRoute({ path: messageData.link });
  }

  if (messageData.eventType === 'PLACE_BET_CLICKED_NOT_LOGGED_IN') {
    openLogin();
  }

  if (messageData.eventType === 'shareBetslip') {
    let path = route.path;

    if (path.startsWith('/')) {
      const secondSlashIndex = path.indexOf('/', 1);

      if (secondSlashIndex !== -1) {
        path = path.slice(secondSlashIndex);
      } else {
        path = '';
      }
    }

    const betSlipLink = `${fullSportsPageUrl.value}${path}?bscode=${messageData.eventData.code}`;

    const { openToaster } = useLayoutStore();

    copyToClipboard(betSlipLink).then((ok) => {
      openToaster({
        message: ok
          ? t('account.wallet.code_copied')
          : t('casino.game.sharing.not_supported'),
        type: ok ? 'info' : 'error',
      });
    });

    cactusSportbooksInstance.sendPostMessage({
      eventType: 'shareBetslip',
      eventData: { url: betSlipLink },
    });
  }

  if (messageData.eventType === 'depositClick') {
    openDepositModal();
  }

  if (messageData.eventType === 'LOCATION_CHANGE') {
    if (isProcessingLocationChange) return;
    isProcessingLocationChange = true;

    const newPath = slugifyPath(messageData);

    if (newPath !== route.path) {
      goToLocaleRoute({ path: newPath });
    }

    setTimeout(() => (isProcessingLocationChange = false), 100);
  }
}

onMounted(async () => {
  window.addEventListener('message', listenPostMessage);

  if (route.query.bscode && !isAuthenticated.value) {
    if (Array.isArray(route.query.bscode)) {
      setBscode(route.query.bscode[0]);
    } else {
      setBscode(route.query.bscode as string);
    }
  }

  await selectCurrentPage();

  if (layoutStore.menuOpen && isLayoutMobileOrTablet.value) {
    layoutStore.toggleMenuOpen();
  }
});

onActivated(async () => {
  await selectCurrentPage();

  if (layoutStore.menuOpen && isLayoutMobileOrTablet.value) {
    layoutStore.toggleMenuOpen();
  }
});

watch(
  () => route.path,
  async (newPath, oldPath) => {
    if (newPath !== oldPath && !isProcessingLocationChange) {
      await selectCurrentPage();
    }
  },
);

onBeforeRouteLeave(() => {
  window.removeEventListener('message', listenPostMessage);
  isTimeoutEnabled.value = false;

  if (!layoutStore.menuOpen && !isLayoutMobileOrTablet.value) {
    layoutStore.toggleMenuOpen();
  }
});
</script>

<template>
  <div :class="$style.cactusSportbook">
    <iframe
      ref="iframeElement"
      :class="$style.frame"
      frameborder="0"
      :src="iframeSrc"
      title="Sportbook Iframe"
    ></iframe>
  </div>
</template>

<style module lang="postcss">
.cactusSportbook {
  @apply flex w-full;
}

.frame {
  @apply w-full;
}
</style>
